import { Component, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

// A page that throws while rendering must not take the whole app down to a
// white screen: the shell (navigation, sub-nav) stays, the content area says
// the page failed, with a way back. The history entry stops naming the
// crashed page, so a reload (bootEntry.restoredEntry) does not reopen it. The
// next navigation renders the next page again.
type Props = { children: ReactNode; pathname: string; onBack: () => void };
type State = { error: Error | null; at: string | null };

class Boundary extends Component<Props, State> {
  state: State = { error: null, at: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error) {
    const at = this.props.pathname;
    this.setState({ at });
    // eslint-disable-next-line no-console
    console.error('Page failed to render', at, error);
    // After HistoryBridge has stamped this page on the entry (its effect runs
    // after this commit): the entry points at the home page instead.
    setTimeout(() => {
      const st = window.history.state as { memPath?: unknown } | null;
      if (st && typeof st === 'object' && st.memPath === at) {
        window.history.replaceState({ ...st, memPath: '/' }, '', '/');
      }
    }, 0);
  }

  componentDidUpdate(prev: Props) {
    if (prev.pathname !== this.props.pathname && this.state.error) this.setState({ error: null, at: null });
  }

  render() {
    const { error, at } = this.state;
    if (error && (at === null || at === this.props.pathname)) {
      return (
        <div role="alert" className="max-w-lg mx-auto mt-10 rounded-lg border border-border-light bg-surface p-4 text-[0.8125rem]">
          <div className="font-semibold text-primary-text">This page failed to load</div>
          <p className="mt-1 text-secondary">Something went wrong while showing it. Nothing was saved or changed by this error.</p>
          <div className="mt-3 flex items-center gap-3">
            <button type="button" onClick={this.props.onBack}
              className="inline-flex items-center h-7 px-2.5 rounded-full bg-primary text-white text-[0.75rem] font-medium focus-ring">
              Back
            </button>
            <Link to="/" className="text-primary text-[0.75rem] font-medium hover:underline focus-ring">Home</Link>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const back = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === 'number' && idx > 0) nav(-1); else nav('/', { replace: true });
  };
  return <Boundary pathname={pathname} onBack={back}>{children}</Boundary>;
}
